<template lang="pug">
dialog(ref='dialog' @keydown.esc.prevent="emit('close')" @mousedown="onMouseDown" @click="onBackdropClick")
    slot
</template>

<script setup lang="ts">
import { onMounted, ref, watch, onUnmounted, nextTick } from "vue";

// ** 기본 모달 제외 (스크롤 가능한 모달 경우) '.modal-scroll' 클래스명 추가하여 사용 (ex. Modal.modal-scroll)

let props = defineProps({
    open: Boolean,
});

const emit = defineEmits(["close"]);
// dialog 엘리먼트 참조
const dialog = ref<HTMLDialogElement | null>(null);

let mouseDownOutside = false;

function isOutsideDialog(e: MouseEvent): boolean {
    if (!dialog.value) return false;
    const rect = dialog.value.getBoundingClientRect();
    return (
        e.clientX < rect.left ||
        e.clientX > rect.right ||
        e.clientY < rect.top ||
        e.clientY > rect.bottom
    );
}

function onMouseDown(e: MouseEvent) {
    mouseDownOutside = e.target === dialog.value && isOutsideDialog(e);
}

function onBackdropClick(e: MouseEvent) {
    if (e.target === dialog.value && mouseDownOutside && isOutsideDialog(e)) {
        emit('close');
    }
    mouseDownOutside = false;
}

// iOS Safari viewport height 계산
const setViewportHeight = () => {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
};

let viewportListenersAttached = false;

const lockBodyScroll = () => {
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
};

const unlockBodyScroll = () => {
    document.body.style.overflow = "";
    document.body.style.position = "";
    document.body.style.height = "";
};

const onFocusIn = () => {
    document.body.style.height = "100dvh";
    setViewportHeight();
};

const onFocusOut = () => {
    document.body.style.height = "";
    setViewportHeight();
};

const attachViewportListeners = () => {
    if (viewportListenersAttached) return;

    window.addEventListener("focusin", onFocusIn);
    window.addEventListener("focusout", onFocusOut);
    window.addEventListener("resize", setViewportHeight);
    viewportListenersAttached = true;
};

const detachViewportListeners = () => {
    if (!viewportListenersAttached) return;

    window.removeEventListener("focusin", onFocusIn);
    window.removeEventListener("focusout", onFocusOut);
    window.removeEventListener("resize", setViewportHeight);
    viewportListenersAttached = false;
};

const resetScroll = () => {
    if (!dialog.value) return;
    dialog.value.scrollTop = 0;
};

const openModal = async () => {
    attachViewportListeners();
    setViewportHeight();

    if (dialog.value && !dialog.value.open) {
        dialog.value.showModal();
    }

    await nextTick();
    requestAnimationFrame(resetScroll);
    lockBodyScroll();
};

const closeModal = () => {
    if (dialog.value?.open) {
        dialog.value.close();
    }

    unlockBodyScroll();
    detachViewportListeners();
};

watch(
    () => props.open,
    async (nv) => {
        if (nv) {
            await openModal();
        } else {
            closeModal();
        }
    }
);

onMounted(async () => {
    if (props.open) {
        await openModal();
    } else {
        setViewportHeight();
    }
});

onUnmounted(() => {
    closeModal();
});
</script>

<style lang="less">
@import "@/assets/less/bq-tokens.less";
dialog,
.dialog {
    border-radius: 0;
    border: 1px solid #d0d0d0;
    outline: none;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.1);
    background-color: @paper;
    color: @ink;
    padding: 3rem;

    overscroll-behavior: contain;
    touch-action: manipulation;
    -webkit-overflow-scrolling: touch;

    &::backdrop {
        background-color: rgba(0, 0, 0, 0.5);
    }
}

@media (max-width: 800px) {
    dialog,
    .dialog {
        padding: 0;
    }
}

@media (max-width: 430px) {
    dialog,
    .dialog {
        max-width: calc(100% - 1rem);
        max-height: calc(100% - 1rem);
    }
}
</style>
